import React from 'react';
import { Container, Typography, Box, List, ListItem, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { ContactInfoImage } from './ContactInfoImage';
import { SEO } from './SEO';

const contentUseCase = new ContentUseCase();

export const PrivacyPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const content = contentUseCase.getPrivacyPolicy(language);

  return (
    <Container maxWidth="xl" sx={{ py: theme.spacing(6) }}>
      <SEO page="privacy" />
      <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: theme.spacing(6) }}>
          {content.title}
        </Typography>

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.responsible.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.responsible.description}
          </Typography>
          <ContactInfoImage language={language} />
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.general.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.general.description}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.general.principle}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.automatic.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.automatic.server.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.automatic.server.description}
          </Typography>
          <Box sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <List>
              {content.automatic.server.data.map((item, index) => (
                <ListItem key={`server-data-${item.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: 0.5 }}>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.automatic.legal}</strong>
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.automatic.retention}</strong>
          </Typography>

          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.automatic.analytics.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.automatic.analytics.description}
          </Typography>
          <Box sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <List>
              {content.automatic.analytics.data.map((item) => (
                <ListItem key={`analytics-data-${item.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: 0.5 }}>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.automatic.analytics.legal}</strong>
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.automatic.analytics.retention}</strong>
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.automatic.analytics.optout}</strong>
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.cookies.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.cookies.necessary.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.cookies.necessary.description}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.cookies.analytics.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.cookies.analytics.description}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.cookies.analytics.legal}</strong>
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.cookies.analytics.optout}</strong>
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.cookies.tracking.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.cookies.tracking.description}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.cookies.legal}</strong>
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.external.title}
          </Typography>
          {content.external.forms && (
            <>
              <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
                {content.external.forms.title}
              </Typography>
              <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
                {content.external.forms.description}
              </Typography>
              {content.external.forms.legal && (
                <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
                  <strong>{content.external.forms.legal}</strong>
                </Typography>
              )}
              {content.external.forms.retention && (
                <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
                  <strong>{content.external.forms.retention}</strong>
                </Typography>
              )}
            </>
          )}
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.payment.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.payment.description}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.external.payment.legal}</strong>
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.donations.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.donations.description}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.social.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.social.description}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.rights.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.rights.description}
          </Typography>
          <Box sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <List>
              {content.rights.list.map((item, index) => (
                <ListItem key={`right-${item.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: 0.5 }}>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.rights.contact}</strong>
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.complaint.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.complaint.description}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.complaint.authority}</strong><br />
            {content.complaint.address}<br />
            {content.complaint.city}<br />
            {content.complaint.email}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.changes.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.changes.description}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {language === 'en' ? 'Last updated:' : 'Stand:'} {new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE')}
          </Typography>
        </Box>
      </Container>
  );
};
